/**
 * Everyone working on a project, as the Team tab lists them.
 *
 * A project is assigned through its five fields, and the `team` array adds the
 * extra people put on the Team tab - the same two sources the API's access
 * predicate reads (see repo.ts). The tab used to show only the array, so a
 * project run entirely by its four named managers read "No team members
 * assigned". This is a list of names, not an access check: the API has
 * already decided who may open the project.
 *
 * One person appears once. Someone who is both Property Manager and on the
 * team array is one entry carrying both labels, and `teamIndex` keeps the
 * position of their array entry so it can still be removed.
 */
import { displayName } from '@/lib/jobTitles';

export const ASSIGNMENT_POSTS = [
  { key: 'property_manager', label: 'Property Manager' },
  { key: 'senior_property_manager', label: 'Senior Property Manager' },
  { key: 'apm', label: 'APM' },
  { key: 'construction_manager', label: 'Construction Manager' },
  { key: 'project_manager_name', label: 'Project Manager' },
];

/**
 * The Team tab's roles, highest level first (Evan, 2026-10-16). The four
 * Westwood roles give exactly what being named in that box gives; Contractor
 * and Tenant are a contact record only - no access, no email.
 *
 * The API holds the same table in api/src/lib/projectTeam.ts and enforces it;
 * api/test/team-roles.test.ts reads this literal and fails if the two differ,
 * so keep one role per line in this shape.
 */
export const TEAM_ROLE_TABLE = [
  { value: 'construction_manager', label: 'Construction Manager', level: 5, access: true },
  { value: 'project_manager', label: 'Project Manager', level: 4, access: true },
  { value: 'property_manager', label: 'Property Manager', level: 3, access: true },
  { value: 'apm', label: 'APM', level: 2, access: true },
  { value: 'contractor', label: 'Contractor', level: 1, access: false },
  { value: 'tenant', label: 'Tenant', level: 1, access: false },
];

export const TEAM_ROLE_LABELS = Object.fromEntries(TEAM_ROLE_TABLE.map(r => [r.value, r.label]));

const ROLE = Object.fromEntries(TEAM_ROLE_TABLE.map(r => [r.value, r]));

/** A role this table does not know counts as the top level, as in the API. */
export function teamRoleLevel(role) {
  return ROLE[role]?.level ?? 5;
}

/** Does this role open the project? Contractor and Tenant do not. */
export function isAccessRole(role) {
  return !!ROLE[role]?.access;
}

/** The level each box on the project form puts the person it names at. */
export const ASSIGNMENT_LEVELS = {
  construction_manager: 5,
  project_manager_name: 4,
  property_manager: 3,
  senior_property_manager: 3,
  apm: 2,
};

/** Creating a project puts you at the top of it. */
const CREATOR_LEVEL = 5;

/**
 * This person's level on this project, 0 for none: the highest of the box
 * naming them, having created it, and a current Westwood role on its Team
 * tab. The API works it out the same way and is what decides.
 */
export function teamLevel(project, user, today = todayPacific()) {
  const email = keyOf(user?.email);
  if (!project || !email) return 0;
  const levels = [0];
  if (user?.id && project.created_by_id && String(project.created_by_id).toLowerCase() === String(user.id).toLowerCase()) {
    levels.push(CREATOR_LEVEL);
  }
  for (const [field, level] of Object.entries(ASSIGNMENT_LEVELS)) {
    if (keyOf(project[field]) === email) levels.push(level);
  }
  for (const m of Array.isArray(project.team) ? project.team : []) {
    if (keyOf(m?.email) === email && isAccessRole(m.role) && !isTeamAccessExpired(m, today)) {
      levels.push(teamRoleLevel(m.role));
    }
  }
  return Math.max(...levels);
}

/**
 * Is this caller the project's Property Manager - in the Property Manager box,
 * the Senior Property Manager box, or through a current Property Manager entry
 * on the Team tab? Named any other way, or having created it, does not count.
 *
 * What the VP of Property Management needs to edit a project (migration 026).
 * Mirrors `namedAsPropertyManager` in api/src/lib/projectTeam.ts, which with
 * its SQL twin in repo.ts is what decides.
 */
export function namedAsPropertyManager(project, user, today = todayPacific()) {
  const email = keyOf(user?.email);
  if (!project || !email) return false;
  if (keyOf(project.property_manager) === email || keyOf(project.senior_property_manager) === email) {
    return true;
  }
  return (Array.isArray(project.team) ? project.team : []).some(m =>
    keyOf(m?.email) === email && m.role === 'property_manager' && !isTeamAccessExpired(m, today));
}

/**
 * The roles this caller may pick in Add Team Member: at their level or
 * below. The administrator tiers pick from all six.
 */
export function grantableTeamRoles(level, isProjectAdmin = false) {
  return TEAM_ROLE_TABLE.filter(r => isProjectAdmin || r.level <= level);
}

/** May this caller change or remove an entry with this role? */
export function canChangeTeamEntry(role, level, isProjectAdmin = false) {
  return isProjectAdmin || (level >= 2 && teamRoleLevel(role) <= level);
}

/** Today's date in Pacific time, 'YYYY-MM-DD' - the day an end date is kept to. */
export function todayPacific(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * Has this entry's access ended? Access runs to the end of the `access_until`
 * day; no date means no end. The API applies the same test when deciding who
 * may open the project, so this only decides how the entry is labelled.
 */
export function isTeamAccessExpired(member, today = todayPacific()) {
  const until = typeof member?.access_until === 'string' ? member.access_until.trim() : '';
  return !!until && until < today;
}

/**
 * The list after adding `member`. One entry per person: adding an address
 * already on the list replaces that entry - a new role, a new end date - and
 * anything else is appended. It used to replace whoever held the same *role*,
 * so adding a second member in one role quietly took the first off the project.
 */
export function withTeamMember(team, member) {
  const list = Array.isArray(team) ? team : [];
  const email = keyOf(member.email);
  const entry = {
    name: String(member.name || '').trim(),
    email: String(member.email || '').trim(),
    role: member.role,
    ...(member.access_until ? { access_until: member.access_until } : {}),
    /* Which contractor on the Contractors list this is (migration 027). Only
       a Contractor entry keeps one; the API refuses it on any other role. */
    ...(member.role === 'contractor' && member.contractor_id ? { contractor_id: member.contractor_id } : {}),
  };
  const idx = list.findIndex(m => keyOf(m?.email) === email);
  return idx >= 0 ? list.map((m, i) => (i === idx ? entry : m)) : [...list, entry];
}

const keyOf = value => String(value || '').trim().toLowerCase();

/**
 * `{ key, name, email, posts: [label], teamRole, teamIndex }` per person:
 * the four posts first in their fixed order, then the Team tab's additions.
 * `users` is the directory, used only to turn an address into a name; a value
 * it does not know - an old project can hold a typed name - is shown as is.
 */
export function projectPeople(project, users = []) {
  const byEmail = new Map((users || []).map(u => [keyOf(u.email), u]));
  const people = [];
  const index = new Map();

  const personFor = (value, fallbackName = '') => {
    const key = keyOf(value);
    if (!key) return null;
    if (index.has(key)) return index.get(key);
    const user = byEmail.get(key);
    const looksLikeEmail = key.includes('@');
    const person = {
      key,
      name: user ? displayName(user) : (fallbackName || String(value).trim()),
      email: user?.email || (looksLikeEmail ? String(value).trim() : ''),
      jobTitle: user?.job_title || '',
      posts: [],
      teamRole: '',
      teamIndex: -1,
      accessUntil: '',
      contractorId: '',
    };
    index.set(key, person);
    people.push(person);
    return person;
  };

  for (const post of ASSIGNMENT_POSTS) {
    const person = personFor(project?.[post.key]);
    if (person) person.posts.push(post.label);
  }

  (Array.isArray(project?.team) ? project.team : []).forEach((member, i) => {
    const person = personFor(member?.email || member?.name, member?.name);
    if (!person || person.teamIndex >= 0) return;
    person.teamRole = member.role || '';
    person.teamIndex = i;
    person.accessUntil = member.access_until || '';
    person.contractorId = member.role === 'contractor' ? (member.contractor_id || '') : '';
  });

  return people;
}
